/** Workspace-level resources: the header image, files and links (see backend `workspaces/resources`). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, api, apiUpload, queryKeys } from '@/shared/api';

import type { Workspace } from './index';

export interface WorkspaceFile {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  /** A verified raster image: `preview` works. */
  previewable: boolean;
  created_at: string;
}

export interface WorkspaceLink {
  id: string;
  url: string;
  title: string | null;
  created_at: string;
}

export const workspaceFileDownloadUrl = (workspaceId: string, fileId: string) =>
  `/api/v1/workspaces/${workspaceId}/files/${fileId}/download`;

export const workspaceFilePreviewUrl = (
  workspaceId: string,
  fileId: string,
  variant: 'thumb' | 'medium' | 'large' | 'original' = 'thumb',
) => `/api/v1/workspaces/${workspaceId}/files/${fileId}/preview?variant=${variant}`;

const base = (workspaceId: string) => `/v1/workspaces/${workspaceId}`;

/** Same limit as the backend upload limit; the server enforces it anyway. */
export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export function useWorkspaceFiles(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.workspaces.files(workspaceId),
    queryFn: ({ signal }) => api.get<WorkspaceFile[]>(`${base(workspaceId)}/files`, { signal }),
  });
}

export function useWorkspaceLinks(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.workspaces.links(workspaceId),
    queryFn: ({ signal }) => api.get<WorkspaceLink[]>(`${base(workspaceId)}/links`, { signal }),
  });
}

export function useDeleteWorkspaceFile(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => api.delete(`${base(workspaceId)}/files/${fileId}`),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: queryKeys.workspaces.files(workspaceId) }),
  });
}

export interface LinkInput {
  url: string;
  title: string | null;
}

export function useSaveWorkspaceLink(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: LinkInput & { id?: string }) =>
      id
        ? api.patch<WorkspaceLink>(`${base(workspaceId)}/links/${id}`, data)
        : api.post<WorkspaceLink>(`${base(workspaceId)}/links`, data),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: queryKeys.workspaces.links(workspaceId) }),
  });
}

export function useDeleteWorkspaceLink(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => api.delete(`${base(workspaceId)}/links/${linkId}`),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: queryKeys.workspaces.links(workspaceId) }),
  });
}

/** Stores an uploaded header image (shown once the saved appearance says `image`). */
export async function uploadHeaderImage(workspaceId: string, file: File): Promise<Workspace> {
  return apiUpload<Workspace>(`${base(workspaceId)}/header-image`, file, {
    filename: file.name || 'header',
    method: 'PUT',
  });
}

export interface WorkspaceUploadItem {
  key: string;
  name: string;
  progress: number;
  error?: unknown;
}

let nextKey = 0;

/** Upload queue of the files panel: one request per file, with progress; failures stay listed. */
export function useWorkspaceFileUploader(workspaceId: string) {
  const client = useQueryClient();
  const [items, setItems] = useState<WorkspaceUploadItem[]>([]);
  const controllers = useRef(new Set<AbortController>());

  useEffect(() => {
    const running = controllers.current;
    return () => {
      for (const controller of running) controller.abort();
    };
  }, []);

  const add = useCallback(
    (files: readonly File[]) => {
      for (const file of files) {
        nextKey += 1;
        const key = `workspace-upload-${nextKey}`;
        const name = file.name || 'file';
        const patch = (change: Partial<WorkspaceUploadItem>) => {
          setItems((current) =>
            current.map((item) => (item.key === key ? { ...item, ...change } : item)),
          );
        };
        if (file.size > MAX_FILE_BYTES) {
          setItems((current) => [
            ...current,
            {
              key,
              name,
              progress: 0,
              error: new ApiError(413, 'MEDIA_TOO_LARGE', 'File is too large'),
            },
          ]);
          continue;
        }
        const controller = new AbortController();
        controllers.current.add(controller);
        setItems((current) => [...current, { key, name, progress: 0 }]);
        apiUpload<WorkspaceFile>(`${base(workspaceId)}/files`, file, {
          filename: name,
          signal: controller.signal,
          onProgress: (loaded, total) => {
            patch({ progress: total > 0 ? loaded / total : 0 });
          },
        }).then(
          () => {
            controllers.current.delete(controller);
            setItems((current) => current.filter((item) => item.key !== key));
            void client.invalidateQueries({ queryKey: queryKeys.workspaces.files(workspaceId) });
          },
          (error: unknown) => {
            controllers.current.delete(controller);
            if (error instanceof DOMException && error.name === 'AbortError') return;
            patch({ error });
          },
        );
      }
    },
    [client, workspaceId],
  );

  const dismiss = useCallback((key: string) => {
    setItems((current) => current.filter((item) => item.key !== key));
  }, []);

  return { items, add, dismiss };
}
